import React from 'react';
import { plata, peso, fecha, hoy } from '../utils/formato';
import { enPesos } from '../utils/letras';
import Membrete from './Membrete';
import { moverPeriodo, nombreDelPeriodo, FRECUENCIAS } from '../utils/periodo';
import { renglonesDeLaCuenta, subtotalesPorUnidad } from '../utils/relacionVenta';
import { useConsecutivo, llaveDeRelacion } from '../utils/consecutivo';

// La relación de venta: la cuenta de un cliente en un periodo.
//
// «Así como se visualizan las facturas de las fincas, que se visualicen las del
// queso y el suero.»
//
// ── Por qué no era la remisión ──
//
// La remisión ya existía y está bien: es UN despacho —un día, un camión, una
// firma al descargar— con su lote impreso. Lo que faltaba era la otra hoja: lo
// que el cliente se llevó en todo el mes, lo que abonó, y en cuánto quedó. La
// finca la tenía desde el principio; el cliente cobraba sin ella.
//
// ── Lo que la hace distinta de la de la leche ──
//
// La quincena de una finca se liquida y se cierra en cero, así que su relación
// puede empezar de cero. La cuenta de un cliente NO se cierra: arrastra. Por
// eso arriba va el SALDO ANTERIOR. Sin él, la hoja del segundo mes diría que
// debe solo lo del segundo mes — y esa es la clase de papel con el que se
// pierde una cartera.

function delNegocio() {
  try {
    const s = JSON.parse(localStorage.getItem('quesera-sesion'));
    const u = s && s.user;
    return { negocio: u?.negocio || null, nit: u?.nit || null,
             direccion: u?.direccion || null, ciudad: u?.ciudad || null,
             telefono: u?.telefono || null, logo: u?.logo || null };
  } catch { return {}; }
}

function RelacionVenta({ r, alCerrar, alCambiarPeriodo }) {
  // ANTES del `return null`, por el orden de los enganches de React.
  // Solo se numera la hoja que TIENE algo. Gabriel va a pasar semanas con las
  // flechas buscando una, y numerar las vacías haría crecer la serie de solo
  // mirar: al mes tendría cien papeles que nunca existieron.
  const hayQueNumerar = !!(r && (r.ventas || []).length > 0);
  const numero = useConsecutivo('RVT',
    hayQueNumerar
      ? llaveDeRelacion('c', r.cliente && r.cliente.id, r.desde, r.hasta) : null,
    r && r.hasta);
  if (!r) return null;
  const c = r.cliente || {};
  const renglones = renglonesDeLaCuenta(r.ventas);
  // El descuento pactado se cobra en cada despacho, así que en un mes son
  // varios. Se suman y se enseñan en un solo renglón: repetirlo despacho por
  // despacho al pie llenaría media hoja con la misma frase.
  //
  // Viene del servidor y NO se suma aquí: solo cuenta el de lo fiado. El
  // descuento de una venta de contado ya se cobró y no mueve la deuda, así que
  // sumarlo aquí haría que la columna no cuadrara justo en el cliente que
  // compra de las dos formas.
  const descuentos = Math.round(Number(r.descuento) || 0);
  // El periodo de ESTE cliente. Antes era siempre el mes: al que se le factura
  // cada quince días había que corregirle las dos fechas a mano cada vez.
  const frec = c.frecuencia_pago || 'mensual';
  const empieza = c.semana_empieza;
  const quincena = c.quincena_empieza;
  const arranque = c.ciclo_desde;
  // Una cifra por unidad. Sumar los litros del suero dentro de los kilos del
  // queso da un total que no significa nada y que desmiente la tabla de arriba.
  const subtotales = subtotalesPorUnidad(renglones);
  const contado = renglones.filter(l => l.contado).reduce((s, l) => s + l.total, 0);

  // ── EL DESCUENTO, EN LA TABLA ───────────────────────────────────────────
  //
  // La hoja de VICTOR decía «SUBTOTALES $41.950.350» cuando se le cobraron
  // $41.716.350. El descuento SÍ se le hizo —$58.500 en cada uno de los cuatro
  // despachos— pero no salía por ningún lado: los renglones se suman en bruto
  // y el descuento vive en la venta, no en la línea.
  //
  // Para un cliente de contado eso lo dejaba invisible, y así estuvo hasta que
  // Gabriel preguntó por qué no se descontaba. Se le estaba descontando; lo que
  // faltaba era decirlo.
  //
  // Van los DOS descuentos juntos —el de lo fiado y el de lo de contado—
  // porque la tabla de arriba enseña TODO lo despachado, sin separar por forma
  // de pago. Poner solo uno dejaría la resta sin cuadrar justo en el cliente
  // que compra de las dos maneras.
  const bruto = renglones.reduce((s, l) => s + l.total, 0);
  const descuentoTotal = descuentos + Math.round(Number(r.descuento_contado) || 0);
  // En cuántos despachos se aplicó. Es lo que deja ver de un vistazo que son
  // «3 kilos en cada uno de los 4 viajes» y no 3 kilos en toda la semana —
  // la diferencia que nadie había notado porque nunca se imprimió.
  const despachosConDescuento = (r.ventas || [])
    .filter(v => Number(v.descuento) > 0).length;

  return (
    <div className="capa-imprimir">
      <div className="barra-imprimir">
        {/* Igual que en la de la leche: casi siempre se saca el mes que acaba
            de cerrar, y corregir dos fechas a mano cada vez es donde se cuela
            el error. */}
        <button className="boton suave"
                onClick={() => alCambiarPeriodo(
                  moverPeriodo({ desde: r.desde, hasta: r.hasta }, frec, -1,
                               empieza, quincena, arranque))}>
          ◀ Anterior
        </button>
        <button className="boton suave"
                onClick={() => alCambiarPeriodo(
                  moverPeriodo({ desde: r.desde, hasta: r.hasta }, frec, 1,
                               empieza, quincena, arranque))}>
          Siguiente ▶
        </button>
        <div style={{ flex: 1 }} />
        <button className="boton suave" onClick={alCerrar}>Cerrar</button>
        <button className="boton" onClick={() => window.print()}>🖨️ Imprimir</button>
      </div>

      <div className="hoja">
        <div className="comprobante">
          <Membrete
            v={{ ...delNegocio(), numero }}
            titulo="RELACIÓN DE VENTA"
            cual={'Emitida el ' + fecha(hoy())} />

          <table className="cuerpo-c" style={{ marginBottom: 14 }}>
            <tbody>
              <tr>
                <td className="rot">Cliente</td>
                <td className="neg">{c.nombre}</td>
                <td className="rot">C.C. o NIT</td>
                <td>{c.documento || '—'}</td>
              </tr>
              <tr>
                <td className="rot">Despacho</td>
                <td>{[c.ciudad, c.direccion].filter(Boolean).join(' · ') || '—'}</td>
                <td className="rot">Periodo</td>
                <td>
                  {nombreDelPeriodo({ desde: r.desde, hasta: r.hasta }, frec, quincena)}:{' '}
                  {fecha(r.desde)} al {fecha(r.hasta)}
                  <div className="chico">
                    Se le factura {(FRECUENCIAS[frec] || 'Mensual').toLowerCase()}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          {renglones.length === 0 ? (
            /* Una tabla vacía se lee como que el programa falló. Decir que no
               hubo despachos es una afirmación, y no saber no es lo mismo que
               saber que no. */
            <p style={{ fontSize: 13, margin: '18px 0' }}>
              En este periodo no se le despachó nada.
            </p>
          ) : (
            <table className="relacion">
              <thead>
                <tr>
                  {/* Las mismas columnas de la relación de leche —DÍA ·
                      DESCRIPCIÓN · CANT · VALOR · TOTAL— para que las dos
                      hojas se lean igual. Donde la leche pone el día suelto,
                      aquí la descripción lleva además el número de la remisión:
                      es lo que permite ir del renglón al papel que se firmó. */}
                  <th>Día</th>
                  <th>Descripción</th>
                  <th className="der">Cant.</th>
                  <th className="der">Valor</th>
                  <th className="der">Total</th>
                </tr>
              </thead>
              <tbody>
                {renglones.map((l, n) => (
                  <tr key={n}>
                    <td>{fecha(l.fecha)}</td>
                    <td>
                      {l.producto}
                      <span className="chico">
                        {' · '}rem. {l.venta}
                        {l.contado ? ' · pagada de contado' : ''}
                      </span>
                      {/* LOS LOTES, DEBAJO Y EN PEQUEÑO.
                          
                          Un renglón por lote convertía un despacho en dieciséis
                          renglones. De cuál lote salió cada kilo es cosa de la
                          quesera, no del cliente: él recibió un camión. Pero no
                          se tiran, porque el día que un queso salga malo son lo
                          único que dice de qué cuajada era. */}
                      {l.lotes && l.lotes.length > 0 && (
                        <div className="chico">
                          {l.lotes.length === 1 ? 'lote ' : 'lotes '}
                          {l.lotes.join(' · ')}
                        </div>
                      )}
                    </td>
                    <td className="der">{peso(l.cantidad, l.unidad)}</td>
                    <td className="der">{plata(l.precio)}</td>
                    <td className="der">{plata(l.total)}</td>
                  </tr>
                ))}
                <tr className="subt">
                  <td colSpan={2}>SUBTOTALES</td>
                  <td className="der">
                    {subtotales.map((x, n) => (
                      <div key={n}>{peso(x.cantidad, x.unidad)}</div>
                    ))}
                  </td>
                  <td></td>
                  <td className="der">{plata(bruto)}</td>
                </tr>

                {/* LOS KILOS NO SE TOCAN.

                    Gabriel pidió que el total dijera 2.148,30 en vez de
                    2.151,30. Pero Víctor SE LLEVÓ los 2.151,30 kilos, y eso es
                    lo que dicen las cuatro remisiones que firmó al descargar.
                    Una factura que enseñe menos kilos de los que salieron
                    contradice sus propios papeles.

                    El acuerdo no es que se lleve menos queso: es que se le
                    cobran menos. El descuento es PLATA, y va aquí. */}
                {descuentoTotal > 0 && (
                  <>
                    <tr>
                      <td colSpan={2}>
                        {/* Lo normal es UNA vez por factura. Pero las ventas
                            guardadas antes del cambio llevan el suyo congelado
                            en cada despacho, y esas facturas tienen que seguir
                            diciendo lo que decían: por eso se cuenta en cuántos
                            cayó en vez de dar el número por hecho. */}
                        Menos descuento acordado
                        {c.descuento_kilos > 0 && despachosConDescuento > 0
                          ? ` — ${peso(c.descuento_kilos, 'kg')}` +
                            (despachosConDescuento === 1
                              ? ' de esta factura'
                              : ` en cada uno de los ${despachosConDescuento} despachos`)
                          : ''}
                      </td>
                      <td></td>
                      <td></td>
                      <td className="der">−{plata(descuentoTotal)}</td>
                    </tr>
                    <tr className="subt">
                      <td colSpan={2}>TOTAL COBRADO</td>
                      <td></td>
                      <td></td>
                      <td className="der">{plata(bruto - descuentoTotal)}</td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          )}

          {/* ── LA CUENTA ─────────────────────────────────────────────────
              Cuatro renglones y en este orden: de dónde venía, qué se llevó,
              qué pagó, en cuánto quedó. Es como se lee una cuenta en voz alta
              cuando se está cobrando. */}
          <table className="detalle" style={{ marginTop: 14 }}>
            <tbody>
              <tr>
                <td>Saldo anterior al {fecha(r.desde)}</td>
                <td className="der">{plata(r.saldo_anterior)}</td>
              </tr>
              {/* Con descuento van DOS renglones: lo que se llevó antes del
                  descuento y el descuento aparte. El total de cada despacho ya
                  viene descontado, así que enseñar el neto y ADEMÁS restar el
                  descuento lo quitaría dos veces — la columna no cuadraría y
                  quien la revisa lo leería como un error de cuentas. */}
              <tr>
                <td>
                  Más despachos fiados del periodo
                  {descuentos > 0 ? ' — antes del descuento' : ''}
                </td>
                <td className="der">
                  {plata(descuentos > 0 ? r.fiado_bruto : r.fiado)}
                </td>
              </tr>
              {descuentos > 0 && (
                <tr>
                  <td>
                    Menos descuento acordado
                    {c.descuento_kilos > 0
                      ? ` — ${peso(c.descuento_kilos, 'kg')} por despacho`
                      : ''}
                  </td>
                  <td className="der">−{plata(descuentos)}</td>
                </tr>
              )}
              {(r.abonos || []).map((a, n) => (
                <tr key={n}>
                  <td>
                    Menos abono del {fecha(a.fecha)}
                    {a.forma ? ` — ${a.forma}` : ''}
                    {a.nota ? ` · ${a.nota}` : ''}
                  </td>
                  <td className="der">−{plata(a.monto)}</td>
                </tr>
              ))}
              {/* SIN ABONOS TAMBIÉN SE DICE, Y SE DICE QUE FUERON CERO.

                  «Aunque no dé ningún abono siempre hay que mostrarle lo que
                  está debiendo y lo que ha abonado, siempre, porque es una
                  manera de que el cliente siempre vea lo que debe.»

                  Antes, un periodo sin pagos simplemente no traía renglón de
                  abonos: la hoja pasaba del fiado al saldo sin mencionarlos.
                  Quien la recibe no puede distinguir «no abonó nada» de «aquí
                  no se están contando sus abonos», y esa duda es justo la que
                  convierte un cobro en una discusión.

                  El cero es una afirmación. La ausencia no dice nada. */}
              {(r.abonos || []).length === 0 && (
                <tr>
                  <td>Abonos recibidos en este periodo — ninguno</td>
                  <td className="der">{plata(0)}</td>
                </tr>
              )}
              {/* Con varios abonos, lo abonado en UNA cifra. Sumar cuatro
                  renglones con el dedo para saber cuánto entregó es justo lo
                  que el cliente va a querer comprobar primero. */}
              {(r.abonos || []).length > 1 && (
                <tr>
                  <td style={{ paddingLeft: 18 }}>
                    <em>Abonado en este periodo — {(r.abonos || []).length} abonos</em>
                  </td>
                  <td className="der"><em>−{plata(r.abonado)}</em></td>
                </tr>
              )}
              <tr className="neto">
                <td>SALDO A LA FECHA</td>
                <td className="der">{plata(r.saldo_final)}</td>
              </tr>
            </tbody>
          </table>

          {/* Lo que se llevó de contado no se cobra, pero se dice: si la hoja
              enseñara 660 kilos arriba y solo cobrara 560, quien la recibe se
              queda preguntando por los otros 100.

              Las dos cifras salen de los MISMOS renglones de la tabla, no del
              neto que trae el servidor. Con descuento el neto es menor, y la
              hoja terminaba diciendo «$3.900.000» en los subtotales y «de los
              $3.783.000 despachados» tres renglones más abajo — dos cifras de
              lo mismo en la misma hoja, que es como se deja de creerle a un
              papel. */}
          {contado > 0 && (
            <p style={{ fontSize: 12, marginTop: 8 }}>
              {/* La cifra NETA, la misma que el TOTAL COBRADO de la tabla.
                  Con la bruta, la hoja enseñaba «$41.950.350» arriba y
                  «$41.716.350 se pagaron de contado» tres renglones abajo: dos
                  cifras de lo mismo en el mismo papel, que es como se deja de
                  creerle a un papel. */}
              De los {plata(bruto - descuentoTotal)}{' '}
              cobrados en este periodo,{' '}
              <strong>{plata(contado - (Number(r.descuento_contado) || 0))}</strong>{' '}
              se pagaron de contado y no entran en la cuenta de arriba.
              {/* El descuento ya no se repite aquí: ahora está en la tabla,
                  con su propio renglón y su total. Decirlo dos veces obligaba
                  a comprobar que las dos frases coincidieran, y el día que no
                  coincidan la hoja se cae sola. */}
            </p>
          )}

          {/* EL RENGLÓN EN LETRAS ES DE LA DEUDA, NO DEL DESPACHO.

              A VICTOR, que paga de contado, la hoja le terminaba diciendo «Son:
              CERO PESOS M/CTE» al pie de una factura de $41.716.350. Es cierto
              —no queda debiendo— pero puesto así, en letras y en negrilla bajo
              un total de cuarenta millones, se lee como que la máquina se
              perdió.

              Cuando no hay deuda se dice lo que sí significa algo: que quedó
              a paz y salvo. */}
          <p style={{ fontSize: 12, marginTop: 10 }}>
            <strong>
              {Number(r.saldo_final) > 0
                ? <>Son: {enPesos(r.saldo_final)}</>
                : Number(r.saldo_final) < 0
                  ? <>Tiene un saldo a favor de {plata(-r.saldo_final)}.</>
                  : <>Queda a paz y salvo: no debe nada de este periodo.</>}
            </strong>
          </p>

          <div className="firmas">
            <div>
              <div className="linea" />
              <div className="chico">Elaborado por</div>
            </div>
            <div>
              <div className="linea" />
              <div className="chico">Recibí conforme — {c.nombre}</div>
              <div className="chico">C.C. {c.documento || ''}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RelacionVenta;
